import { useCallback, useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import { ja } from "react-day-picker/locale";
import "react-day-picker/style.css";
import styled from "styled-components";
import { CALENDAR_BREAKPOINT } from "../../constants/app";
import { Button } from "./Button";
import { ArrowIcon } from "./icons/ArrowIcon";
import { CheckIcon } from "./icons/CheckIcon";

type Props = {
  uuid: string;
  defaultDate?: Date;
  defaultTime?: number;
  onCancel: () => void;
  onSelect: (date: Date, time: number) => void;
};

export const Calendar: React.FC<Props> = ({
  uuid,
  defaultDate,
  defaultTime,
  onCancel,
  onSelect,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [currentMonth, setCurrentMonth] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<number>();

  useEffect(() => {
    if (defaultDate) {
      setSelectedDate(defaultDate);
      setCurrentMonth(defaultDate);
    }

    if (defaultTime) setSelectedTime(defaultTime);
  }, []);

  const onClickToday = useCallback(() => {
    setSelectedDate(new Date());
    setCurrentMonth(new Date());
  }, []);

  return (
    <StyledContainer id={`calendar-${uuid}`}>
      <StyledCalendarContainer id={`calendar-container-${uuid}`}>
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          showOutsideDays
          timeZone="Asia/Tokyo"
          ISOWeek
          fixedWeeks
          locale={ja}
          month={currentMonth}
          onDayClick={setCurrentMonth}
          components={{
            CaptionLabel: (span) => {
              const [month, year] = ((span.children as string) ?? "").split(
                " ",
              );
              return (
                <StyledDatePickerHeader id={`header-${uuid}`}>
                  {year}年{month}
                  <Button
                    uuid={`today-${uuid}`}
                    variant="link"
                    text="今日"
                    onClick={onClickToday}
                  />
                </StyledDatePickerHeader>
              );
            },
            Nav: ({ onNextClick, onPreviousClick }) => {
              return (
                <div id={`nav-${uuid}`} className="rdp-nav" aria-label="">
                  <StyledArrowIcon
                    id={`prev-${uuid}`}
                    type="button"
                    onClick={(e) => {
                      setCurrentMonth(undefined);
                      onPreviousClick?.(e);
                    }}
                    $isLeft
                  >
                    <ArrowIcon uuid={`arrow-left-${uuid}`} />
                  </StyledArrowIcon>

                  <StyledArrowIcon
                    id={`next-${uuid}`}
                    type="button"
                    onClick={(e) => {
                      setCurrentMonth(undefined);
                      onNextClick?.(e);
                    }}
                  >
                    <ArrowIcon uuid={`arrow-right-${uuid}`} />
                  </StyledArrowIcon>
                </div>
              );
            },
          }}
        />

        <StyledTimeSelect id={`time-select-${uuid}`}>
          {Array.from({ length: 24 }, (_, i) => {
            return (
              <StyledTimeItem
                key={i}
                id={`time-${i}-${uuid}`}
                onClick={() => setSelectedTime(i)}
              >
                <CheckIcon
                  uuid={`check-${i}-${uuid}`}
                  fillColor={selectedTime === i ? "#000000" : "transparent"}
                />
                <span id={`time-text-${i}-${uuid}`}>{i}:00</span>
              </StyledTimeItem>
            );
          })}
        </StyledTimeSelect>
      </StyledCalendarContainer>

      <StyledFooter id={`footer-${uuid}`}>
        <Button
          uuid={`cancel-${uuid}`}
          type="button"
          text="キャンセル"
          variant="dark"
          onClick={onCancel}
        />
        <Button
          uuid={`select-${uuid}`}
          type="button"
          text="設定"
          variant="filled"
          onClick={() => onSelect(selectedDate!, selectedTime!)}
          disabled={!selectedDate || selectedTime === undefined}
        />
      </StyledFooter>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 10px;
`;

const StyledCalendarContainer = styled.div`
  @media (max-width: ${CALENDAR_BREAKPOINT}) {
    flex-direction: column;
  }

  display: flex;
  gap: 10px;
`;

const StyledTimeSelect = styled.div`
  @media (max-width: ${CALENDAR_BREAKPOINT}) {
    max-width: 172px;
    max-height: 100px;
    align-items: flex-start;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  margin: 10px;
  max-height: 225px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`;

const StyledTimeItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px 5px 8px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background-color: #f2f2f2;
  }

  span {
    font-size: 13px;
    font-weight: 400;
    line-height: normal;
  }
`;

const StyledDatePickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  font-weight: 400;
  font-size: 14px;
  gap: 10px;

  button {
    min-width: 0;
    width: 30px;
    padding: 0;
    margin: 0;
  }
`;

const StyledArrowIcon = styled.button<{ $isLeft?: boolean }>`
  transform: ${({ $isLeft }) => ($isLeft ? "rotate(90deg)" : "rotate(270deg)")};
`;

const StyledFooter = styled.div`
  @media (max-width: ${CALENDAR_BREAKPOINT}) {
    justify-content: center;
    gap: 8px;

    button {
      width: 100%;
    }
  }

  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  margin-bottom: 20px;

  button {
    width: 110px;
    height: 40px;
    padding: 0;
  }
`;
